import React from 'react';
import { Button, Dialog, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import { MultiChipTextField } from '../../keyword-assist/components/MultiChipTextField';

interface GenerateBlogPostDialogProps {
  open: boolean;
  onClose: () => void;
  keywords: string[];
  setKeywords: React.Dispatch<React.SetStateAction<string[]>> | ((kws: string[]) => void);
  onEnable: (kws: string[]) => void;
  title?: string;
  submitText?: string;
}

export const GenerateBlogPostDialog: React.FC<GenerateBlogPostDialogProps> = ({
  open,
  onClose,
  keywords,
  setKeywords,
  onEnable,
  title = "Enable Blog Generation",
  submitText = "Enable"
}) => {
  const MAX_KEYWORDS = 5;
  const [showError, setShowError] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setShowError(false);
      setIsSubmitting(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (keywords.length > 0) {
      setShowError(false);
    }
  }, [keywords]);

  const handleEnable = () => {
    if (keywords.length === 0) {
      setShowError(true);
    } else {
      setShowError(false);
      setIsSubmitting(true);
      onEnable(keywords);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} disableScrollLock>
      <div className="p-6 min-w-[600px] max-w-[600px] relative">
        <p className="text-primary font-semibold text-xl mb-3">
          {title}
        </p>

        <div className="mb-4">
          <TitleTooltip>
            <TitleTooltip.Text
              title={`Enter up to ${MAX_KEYWORDS} main Topics`}
              className="!text-[var(--color-text-secondary)] text-sm font-medium inline-block mb-1.5"
            >
              <TitleTooltip.Tooltip
                tooltip={
                  <p className="text-primary text-sm font-medium">
                    Add primary keywords or topics you want the AI to base your blog posts on.
                  </p>
                }
              />
            </TitleTooltip.Text>
          </TitleTooltip>

          <MultiChipTextField
            values={keywords}
            setValues={setKeywords}
            maxLimit={MAX_KEYWORDS}
            placeholder="Enter keywords (comma-separated)"
            error={showError}
            autoFocus
          />
          {showError && (
            <p className="text-[#D32F2F] text-xs mt-1 font-medium ml-1">
              Please enter at least 1 keyword.
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            className="h-11 flex-1"
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&:hover': {
                borderColor: '#D0D5DD',
                backgroundColor: 'transparent',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            className="h-11 flex-1"
            variant="contained"
            onClick={handleEnable}
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#0F0A3C',
              color: '#FFFFFF',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&:hover': {
                backgroundColor: '#0F0A3C',
              }
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : submitText}
          </Button>
        </div>

        <div aria-label="dialog-close-btn" className="absolute top-0 right-0">
          <IconButton
            aria-label="close"
            className="hover:rounded-lg"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 2,
              height: '40px',
              padding: '8px',
              paddingRight: '8px'
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    </Dialog>
  );
};
